import { IncidentInterface, IncidentReportInterface } from "@type/workMonitor.types"
import { FormSwitchInput, DateField, ImageField, Icon, FormInputAntd, FormSelectAntd } from "components"
import { FormProvider, useForm } from "react-hook-form"
import React, { useContext, useEffect, useState } from "react"
import { yupResolver } from "@hookform/resolvers/yup"
import { incidentReportModalInitialValues, incidentReportSchema } from "Schema/workMonitor/workMonitorSchema"
import useAppDispatch from "hooks/useAppDispatch"
import { addIncidentInShift } from "store/workMonitor/workMonitor.slice"
import useAppSelector from "hooks/useAppSelector"
import { clearMedia, selectLoadingMedia, selectMediaURL, uploadMedia } from "store/Media/media.slice"
import { selectCurrentUser } from "store/auth.slice"
import { SDIncidentsOptions } from "store/StaticData/SDIncidents.slice"
import { locationIncidentSchema } from "Schema/Locations/locationSchema"
import AntdModal from "components/AntdModal"
import ShiftEventsRetainContext, { ShiftEventsContextProps } from "pages/schedules/Location/ShiftEventsContext"
import { selectAllEmployeeDropDown } from "store/Employee/detail.slice"
import { selectAllLocationsForDropdown } from "store/Location/locationDetails.slice"

export interface ShiftIncidentProps {
	shiftId: string
	employeeId: string
	siteId: string
}
interface Props {
	open: boolean
	modalHandler: (open: boolean) => void
	selectedRow: ShiftIncidentProps | null
	loading?: boolean
	isForLocation?: boolean
	defaultFields?: IncidentInterface
	submitHandler?: (data: any) => void
	siteList?: {
		_id: string
		name: string
	}[]
}
const IncidentModal: React.FC<Props> = ({
	open,
	modalHandler,
	selectedRow,
	loading = false,
	isForLocation = false,
	defaultFields = undefined,
	submitHandler = undefined,
	siteList = [],
}) => {
	const methods = useForm<IncidentReportInterface>({
		resolver: yupResolver(isForLocation ? locationIncidentSchema : incidentReportSchema),
		defaultValues: incidentReportModalInitialValues,
	})
	const {
		handleSubmit,
		reset,
		setValue,
		setError,
		clearErrors,
		watch,
		formState: { errors, isSubmitSuccessful },
	} = methods
	const policeRequestedIsEnabled = watch("isPoliceRequested")
	const dispatch = useAppDispatch()
	const [userImage, setUserImage] = useState<string[]>([])
	const imageURL = useAppSelector(selectMediaURL())
	const isImageLoading = useAppSelector(selectLoadingMedia())
	const user = useAppSelector(selectCurrentUser())
	const SDIncidentsDropdownOptions = useAppSelector(SDIncidentsOptions)
	const selectEmployeeList = useAppSelector(selectAllEmployeeDropDown())
	const sitesDropdownList = useAppSelector(selectAllLocationsForDropdown())
	const { fetchShiftByScheduleId } = useContext(ShiftEventsRetainContext) as ShiftEventsContextProps

	useEffect(() => {
		if (isForLocation) {
			const { ...rest } = { ...defaultFields }
			const resetFields: any = defaultFields
				? {
						...rest,
						officer: defaultFields?.officer?._id ?? null,
						reportingOfficer: defaultFields?.reportingOfficer?._id ?? null,
				  }
				: { images: [], isPoliceRequested: false, controlInformed: false, actionTaken: false }

			reset(resetFields)

			setUserImage([])
		}
		if (selectedRow) {
			reset(incidentReportModalInitialValues)
			setValue("officer", selectedRow.employeeId)
			setValue("site", selectedRow.siteId)
			setUserImage([])
		}
		if (user) {
			setValue("reportingOfficer", `${user?.firstName} ${user?.lastName}`)
		}
	}, [selectedRow, isSubmitSuccessful, user, defaultFields])

	useEffect(() => {
		if (imageURL.length) {
			setUserImage(prevState => [...prevState, ...imageURL])
			dispatch(clearMedia())
		}
	}, [imageURL])

	const onSubmit = (data: IncidentReportInterface) => {
		const payload = {
			...data,
			reportingOfficer: user?._id || "",
			images: [...data.images, ...userImage],
		}

		if (isForLocation) submitHandler?.(payload)
		else if (selectedRow?.shiftId)
			dispatch(
				addIncidentInShift(selectedRow?.shiftId, payload, () => fetchShiftByScheduleId(selectedRow.shiftId)),
			)

		modalHandler(false)
	}

	const handleDeleteImage = (imageUrl: string) => {
		setUserImage(prevState => prevState.filter(url => url !== imageUrl))
	}

	return (
		<AntdModal
			open={open}
			title={"Report Incident"}
			onCancel={() => modalHandler(false)}
			okText="Report"
			width={800}
			onOk={handleSubmit(onSubmit)}
		>
			<div>
				{loading ? (
					<div className="flex h-full w-full items-center justify-center">
						<Icon name="spinner" color="white" size={50} className="mr-3 animate-spin !text-purple" />
					</div>
				) : (
					<FormProvider {...methods}>
						<form>
							<div className="grid grid-cols-2 gap-x-4 gap-y-5 py-3">
								{!isForLocation && (
									<>
										<FormSelectAntd
											options={(sitesDropdownList.length ? sitesDropdownList : siteList).map(
												({ _id, name }) => ({
													label: name,
													value: _id,
												}),
											)}
											disabled={!isForLocation ? true : false}
											label="Site Name"
											name="site"
										/>
										<FormSelectAntd
											disabled={!isForLocation ? true : false}
											options={selectEmployeeList}
											label="Security Officer Name"
											name="officer"
										/>
									</>
								)}
								<DateField
									label="Date and Time of Incident"
									name="date"
									errors={errors}
									clearErrors={clearErrors}
									setValue={setValue}
									watch={watch}
									showTime={true}
								/>
								<FormInputAntd
									label="Reporting Officer Name"
									type="text"
									name="reportingOfficer"
									disabled={true} // doing this temporarily
								/>
							</div>
							<div className="col-span-2 flex transform items-center space-x-2">
								<div className="min-w-fit font-semibold">Incident Details</div>
								<div className="w-full border-b-[0.5px] border-secondary-light" />
							</div>
							<div className="grid grid-cols-2 gap-x-4 py-3 ">
								<FormInputAntd label="Incident" name="incident" type="text" />
								<FormSelectAntd
									disabled={!SDIncidentsDropdownOptions.length}
									label="Type"
									name="type"
									options={SDIncidentsDropdownOptions}
								/>
							</div>
							<div className="grid grid-cols-3 gap-x-4 py-3 ">
								<FormSwitchInput
									containerClass="flex items-center w-60"
									label="Action Taken"
									name="actionTaken"
									watch={watch}
									errors={errors}
									setValue={setValue}
								/>
								<FormSwitchInput
									containerClass="flex items-center w-60"
									label="Control Informed"
									name="controlInformed"
									watch={watch}
									errors={errors}
									setValue={setValue}
								/>
								<FormSwitchInput
									containerClass="flex items-center w-60"
									label="Police Requested"
									name="isPoliceRequested"
									watch={watch}
									errors={errors}
									setValue={setValue}
								/>
								<div className="col-span-3 mt-3 grid grid-cols-3 gap-x-5">
									<div className={"col-span-2"}>
										<FormInputAntd type="textarea" label="Details of Incident" name="details" />
									</div>
									<FormInputAntd
										disabled={!policeRequestedIsEnabled}
										label="Reference"
										name="reference"
										type="text"
									/>
								</div>
							</div>
							<div className="flex items-center">
								<ImageField
									name="images"
									containerClass="h-16 w-32"
									loading={isImageLoading}
									image={userImage}
									setError={setError}
									errors={errors}
									uploadMultiple={true}
									clearErrors={clearErrors}
									onImageChange={file => {
										if (!errors["images"]) {
											dispatch(uploadMedia(file))
										}
									}}
									handleDeleteImage={handleDeleteImage}
								/>
							</div>
						</form>
					</FormProvider>
				)}
			</div>
		</AntdModal>
	)
}

export default IncidentModal
